import { FC, FormEvent, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Label, Spinner } from "flowbite-react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineChevronDown,
  HiOutlineLogout,
  HiOutlinePause,
  HiOutlinePlay,
} from "react-icons/hi";
import {
  Button,
  Dialog,
  DropdownButton,
  StyledSelect,
  StyledTextInput,
  TabButton,
} from "@components";
import { useDrivers, useLocations, useProducts } from "@data";
import {
  disconnectShopifyConnection,
  dismissShopifyImport,
  getShopifyImportHealth,
  listShopifyConnections,
  previewShopifyProducts,
  saveShopifyProductMappings,
  ShopifyConnection,
  ShopifyImportHealth,
  ShopifyProductMappingPreview,
  startShopifyOAuth,
  retryShopifyImport,
  restoreShopifyImport,
  updateShopifyConnection,
} from "../../api";
import { useUserSession } from "../../context/UserContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { Driver } from "../../models";
import { SettingsRow } from "./components/SettingsRow";

interface Props {
  logoSrc: string;
}

const SHOPIFY_IMPORT_ERROR_KEYS: Record<string, string> = {
  configuration_incomplete:
    "workspace:integrations.shopify.import_errors.configuration_incomplete",
  connection_inactive:
    "workspace:integrations.shopify.import_errors.connection_inactive",
  connection_not_found:
    "workspace:integrations.shopify.import_errors.connection_not_found",
  integration_unavailable:
    "workspace:integrations.shopify.import_errors.integration_unavailable",
  invalid_order: "workspace:integrations.shopify.import_errors.invalid_order",
  invalid_order_identity:
    "workspace:integrations.shopify.import_errors.invalid_order_identity",
  missing_customer_identity:
    "workspace:integrations.shopify.import_errors.missing_customer_identity",
  missing_line_item_title:
    "workspace:integrations.shopify.import_errors.missing_line_item_title",
  missing_order_id:
    "workspace:integrations.shopify.import_errors.missing_order_id",
  missing_shipping_address:
    "workspace:integrations.shopify.import_errors.missing_shipping_address",
  missing_shipping_city:
    "workspace:integrations.shopify.import_errors.missing_shipping_city",
  missing_shipping_postcode:
    "workspace:integrations.shopify.import_errors.missing_shipping_postcode",
  no_shippable_items:
    "workspace:integrations.shopify.import_errors.no_shippable_items",
  attempts_exhausted:
    "workspace:integrations.shopify.import_errors.attempts_exhausted",
  unexpected_error:
    "workspace:integrations.shopify.import_errors.unexpected_error",
};

const SHOPIFY_CONTROL_CLASS =
  "!bg-white !border-gray-300 shadow-sm focus:!border-brand focus:ring-1 focus:ring-brand dark:!bg-surface-input dark:!border-border-strong";
const SHOPIFY_APP_STORE_URL =
  process.env.REACT_APP_SHOPIFY_APP_STORE_URL?.trim() ?? "";
const SHOPIFY_MANAGED_INSTALL =
  process.env.REACT_APP_SHOPIFY_MANAGED_INSTALL === "true";

const ShopifyIntegrationContent: FC<Props> = ({ logoSrc }) => {
  const { t } = useTranslation(["workspace", "common"]);
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const token = userSession?.token?.accessToken;
  const workspaceId = activeWorkspace?.workspaceId;
  const locations = useLocations().data ?? [];
  const drivers = useDrivers().data ?? [];
  const products = useProducts().data ?? [];
  const [connection, setConnection] = useState<ShopifyConnection | null>(null);
  const [loadingConnection, setLoadingConnection] = useState(true);
  const [shopDomain, setShopDomain] = useState("");
  const [pickupLocationId, setPickupLocationId] = useState("");
  const [defaultDriverId, setDefaultDriverId] = useState("");
  const [defaultProductId, setDefaultProductId] = useState("");
  const [defaultProjectNumber, setDefaultProjectNumber] = useState("");
  const [importEnabled, setImportEnabled] = useState(true);
  const [importMode, setImportMode] = useState<
    "local_delivery" | "all_shippable"
  >("local_delivery");
  const [preview, setPreview] = useState<ShopifyProductMappingPreview | null>(
    null,
  );
  const [mappingValues, setMappingValues] = useState<Record<string, string>>(
    {},
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [importHealth, setImportHealth] = useState<ShopifyImportHealth | null>(
    null,
  );
  const [loadingImportHealth, setLoadingImportHealth] = useState(false);
  const [importHealthError, setImportHealthError] = useState("");
  const [retryingEventId, setRetryingEventId] = useState("");
  const [dismissingEventId, setDismissingEventId] = useState("");
  const [restoringEventId, setRestoringEventId] = useState("");
  const [dismissConfirmEventId, setDismissConfirmEventId] = useState("");
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState<
    "general" | "products" | "activity"
  >("general");

  useEffect(() => {
    if (!token || !workspaceId) return;
    setLoadingConnection(true);
    const load = async () => {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const connections = await listShopifyConnections(token, workspaceId);
        const current = connections.find(
          (item) => item.status !== "disconnected",
        );
        if (current || attempt === 2) return current ?? null;
        await new Promise((resolve) =>
          window.setTimeout(resolve, 500 * (attempt + 1)),
        );
      }
      return null;
    };
    load()
      .then(setConnection)
      .catch((requestError) => {
        // Firebase PR previews use the shared dev API. During a frontend-first
        // rollout that API may not expose the new connection endpoint yet. A
        // 404 OPTIONS response is surfaced by the browser as a CORS network
        // error, so no HTTP status is available to Axios in that case.
        const isPullRequestPreview =
          window.location.hostname.endsWith(".web.app") &&
          window.location.hostname.includes("--pr-");
        if (
          axios.isAxiosError(requestError) &&
          (requestError.response?.status === 404 ||
            (isPullRequestPreview && !requestError.response))
        ) {
          setConnection(null);
          return;
        }
        setError(t("workspace:integrations.shopify.load_failed"));
      })
      .finally(() => setLoadingConnection(false));
  }, [token, workspaceId, t]);

  useEffect(() => {
    if (!connection) return;
    setShopDomain(connection.shopDomain);
    setPickupLocationId(connection.pickupLocationId ?? "");
    setDefaultDriverId(connection.defaultDriverId ?? "");
    setDefaultProductId(connection.defaultProductId ?? "");
    setDefaultProjectNumber(connection.defaultProjectNumber ?? "");
    setImportEnabled(connection.importEnabled ?? true);
    setImportMode(connection.importMode ?? "local_delivery");
  }, [connection]);

  const connectionIsActive =
    connection?.status === "active" && connection.webhookStatus === "active";

  const refreshImportHealth = useCallback(
    async (showLoading = false) => {
      if (!token || !workspaceId || !connection || !connectionIsActive) {
        setImportHealth(null);
        return;
      }
      if (showLoading) setLoadingImportHealth(true);
      setImportHealthError("");
      try {
        setImportHealth(
          await getShopifyImportHealth(token, workspaceId, connection.id),
        );
      } catch {
        setImportHealthError(
          t("workspace:integrations.shopify.health_load_failed"),
        );
      } finally {
        if (showLoading) setLoadingImportHealth(false);
      }
    },
    [token, workspaceId, connection, connectionIsActive, t],
  );

  useEffect(() => {
    if (!token || !workspaceId || !connection || !connectionIsActive) {
      setImportHealth(null);
      return;
    }
    void refreshImportHealth(true);
  }, [token, workspaceId, connection, connectionIsActive, refreshImportHealth]);

  useEffect(() => {
    if (!importHealth?.pendingCount) return;
    const interval = window.setInterval(() => {
      void refreshImportHealth();
    }, 3000);
    return () => window.clearInterval(interval);
  }, [importHealth?.pendingCount, refreshImportHealth]);

  const connect = async (event: FormEvent) => {
    event.preventDefault();
    if (!token || !workspaceId || !shopDomain.trim()) return;
    setBusy(true);
    setError("");
    try {
      const result = await startShopifyOAuth(token, workspaceId, shopDomain);
      window.location.assign(result.authorizationUrl);
    } catch {
      setError(t("workspace:integrations.shopify.connect_failed"));
      setBusy(false);
    }
  };

  const persistConfiguration = async (
    overrides: { importEnabled?: boolean } = {},
  ) => {
    if (!token || !workspaceId || !connection) return false;
    const nextImportEnabled = overrides.importEnabled ?? importEnabled;
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const updated = await updateShopifyConnection(
        token,
        workspaceId,
        connection.id,
        {
          customerId: null,
          pickupLocationId,
          defaultDriverId: defaultDriverId || null,
          defaultProductId: defaultProductId || null,
          defaultProjectNumber: defaultProjectNumber || null,
          productSyncEnabled: true,
          importEnabled: nextImportEnabled,
          importMode,
        },
      );
      setConnection(updated);
      setImportEnabled(updated.importEnabled ?? nextImportEnabled);
      setSaved(true);
      return true;
    } catch {
      setError(t("workspace:integrations.shopify.save_failed"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveConfiguration = async (event: FormEvent) => {
    event.preventDefault();
    await persistConfiguration();
  };

  const pauseImports = async () => {
    const succeeded = await persistConfiguration({ importEnabled: false });
    if (succeeded) setPauseConfirmOpen(false);
  };

  const resumeImports = async () => {
    await persistConfiguration({ importEnabled: true });
  };

  const disconnect = async () => {
    if (!token || !workspaceId || !connection) return;
    setBusy(true);
    setError("");
    try {
      await disconnectShopifyConnection(token, workspaceId, connection.id);
      setDisconnectConfirmOpen(false);
      setConnection(null);
      setPreview(null);
    } catch {
      setError(t("workspace:integrations.shopify.disconnect_failed"));
    } finally {
      setBusy(false);
    }
  };

  const integrationActions = () => (
    <DropdownButton
      placement="bottom-end"
      buttonText={t("common:actions")}
      buttonSize="sm"
      buttonVariant="secondary"
      disabled={busy}
      buttonAppendIcon={<HiOutlineChevronDown className="ml-2 h-4 w-4" />}
      items={[
        {
          text: (
            <div className="flex w-48 items-center gap-2 p-4">
              {importEnabled ? (
                <HiOutlinePause className="h-4 w-4" />
              ) : (
                <HiOutlinePlay className="h-4 w-4" />
              )}
              <span>
                {importEnabled
                  ? t("workspace:integrations.shopify.pause_import")
                  : t("workspace:integrations.shopify.resume_import")}
              </span>
            </div>
          ),
          onClick: () => {
            if (importEnabled) {
              setPauseConfirmOpen(true);
            } else {
              void resumeImports();
            }
          },
        },
        {
          text: (
            <div className="flex w-48 items-center gap-2 p-4 text-danger hover:text-danger-hover">
              <HiOutlineLogout className="h-4 w-4" />
              <span>{t("workspace:integrations.shopify.disconnect")}</span>
            </div>
          ),
          onClick: () => setDisconnectConfirmOpen(true),
        },
      ]}
    />
  );

  const loadProducts = async () => {
    if (!token || !workspaceId || !connection) return;
    setBusy(true);
    setError("");
    try {
      const result = await previewShopifyProducts(
        token,
        workspaceId,
        connection.id,
      );
      setPreview(result);
      setMappingValues(
        Object.fromEntries(
          result.items.map((item) => [
            item.variant.variantId,
            item.status === "ignored"
              ? "__ignored__"
              : item.logibudProductId ??
                (defaultProductId || item.suggestedLogibudProductId || ""),
          ]),
        ),
      );
    } catch {
      setError(t("workspace:integrations.shopify.products_failed"));
    } finally {
      setBusy(false);
    }
  };

  const saveMappings = async () => {
    if (!token || !workspaceId || !connection || !preview) return;
    setBusy(true);
    setError("");
    try {
      const result = await saveShopifyProductMappings(
        token,
        workspaceId,
        connection.id,
        preview.items
          .filter((item) => mappingValues[item.variant.variantId])
          .map((item) => ({
            shopifyVariantId: item.variant.variantId,
            ignored: mappingValues[item.variant.variantId] === "__ignored__",
            logibudProductId:
              mappingValues[item.variant.variantId] === "__ignored__"
                ? null
                : mappingValues[item.variant.variantId],
          })),
      );
      setPreview(result);
      setSaved(true);
    } catch {
      setError(t("workspace:integrations.shopify.products_save_failed"));
    } finally {
      setBusy(false);
    }
  };

  const retryImport = async (eventId: string) => {
    if (!token || !workspaceId || !connection) return;
    setRetryingEventId(eventId);
    setImportHealthError("");
    try {
      await retryShopifyImport(token, workspaceId, connection.id, eventId);
      await refreshImportHealth();
    } catch {
      setImportHealthError(t("workspace:integrations.shopify.retry_failed"));
    } finally {
      setRetryingEventId("");
    }
  };

  const dismissImport = async (eventId: string) => {
    if (!token || !workspaceId || !connection) return;
    setDismissingEventId(eventId);
    setImportHealthError("");
    try {
      await dismissShopifyImport(token, workspaceId, connection.id, eventId);
      setDismissConfirmEventId("");
      await refreshImportHealth();
    } catch {
      setImportHealthError(t("workspace:integrations.shopify.dismiss_failed"));
    } finally {
      setDismissingEventId("");
    }
  };

  const restoreImport = async (eventId: string) => {
    if (!token || !workspaceId || !connection) return;
    setRestoringEventId(eventId);
    setImportHealthError("");
    try {
      await restoreShopifyImport(token, workspaceId, connection.id, eventId);
      await refreshImportHealth();
    } catch {
      setImportHealthError(t("workspace:integrations.shopify.restore_failed"));
    } finally {
      setRestoringEventId("");
    }
  };

  const importErrorMessage = (errorCode?: string | null) => {
    const translationKey = SHOPIFY_IMPORT_ERROR_KEYS[errorCode ?? ""];
    return translationKey
      ? t(translationKey)
      : t("workspace:integrations.shopify.unknown_error");
  };

  const lastOrderReceivedAt =
    connection?.lastWebhookAt ||
    importHealth?.events.find((event) => event.receivedAt)?.receivedAt;
  const lastSuccessfulImportAt =
    connection?.lastSuccessfulSyncAt ||
    importHealth?.events.find((event) => event.status === "succeeded")
      ?.processedAt;
  const displayedImportStatus = importEnabled
    ? importHealth?.status || "idle"
    : "paused";

  return (
    <div className="space-y-6">
      <Dialog
        show={Boolean(dismissConfirmEventId)}
        size="lg"
        title={t("workspace:integrations.shopify.dismiss_confirm_title")}
        toggleModal={() => setDismissConfirmEventId("")}
        isLoading={Boolean(dismissingEventId)}
        dismissible={!dismissingEventId}
        buttonConfirmText={t(
          "workspace:integrations.shopify.dismiss_confirm_action",
        )}
        buttonCloseText={t("common:cancel")}
        confirmButtonColor="failure"
        confirmButton={() => dismissImport(dismissConfirmEventId)}
        content={
          <p className="text-sm text-secondary">
            {t("workspace:integrations.shopify.dismiss_confirm_description")}
          </p>
        }
      />
      <Dialog
        show={pauseConfirmOpen}
        size="lg"
        title={t("workspace:integrations.shopify.pause_confirm_title")}
        toggleModal={() => setPauseConfirmOpen(false)}
        isLoading={busy}
        dismissible={!busy}
        buttonConfirmText={t(
          "workspace:integrations.shopify.pause_confirm_action",
        )}
        buttonConfirmPrependIcon={<HiOutlinePause className="mr-2 h-4 w-4" />}
        buttonCloseText={t("common:cancel")}
        confirmButtonColor="primary"
        confirmButton={pauseImports}
        content={
          <p className="text-sm text-secondary">
            {t("workspace:integrations.shopify.pause_confirm_description")}
          </p>
        }
      />
      <Dialog
        show={disconnectConfirmOpen}
        size="lg"
        title={t("workspace:integrations.shopify.disconnect_title")}
        toggleModal={() => setDisconnectConfirmOpen(false)}
        isLoading={busy}
        dismissible={!busy}
        buttonConfirmText={t("workspace:integrations.shopify.disconnect")}
        buttonConfirmPrependIcon={<HiOutlineLogout className="mr-2 h-4 w-4" />}
        buttonCloseText={t("common:cancel")}
        confirmButtonColor="failure"
        confirmButton={disconnect}
        content={
          <p className="text-sm text-secondary">
            {t("workspace:integrations.shopify.disconnect_description")}
          </p>
        }
      />
      <header className="flex flex-wrap items-center justify-between gap-5">
        <div className="flex items-center gap-5">
          <div className="flex h-16 w-32 items-center justify-center rounded-lg bg-white p-3">
            <img
              src={logoSrc}
              alt="Shopify"
              className="max-h-full max-w-full"
            />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-text">Shopify</h1>
              {connection && !loadingConnection && (
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${
                    connectionIsActive
                      ? connection.importEnabled
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-700"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {connectionIsActive
                    ? connection.importEnabled
                      ? t("workspace:integrations.shopify.connected")
                      : t("workspace:integrations.shopify.paused")
                    : t("workspace:integrations.shopify.action_required")}
                </span>
              )}
            </div>
            <p className="mt-1 text-secondary">
              {t("workspace:integrations.shopify.description")}
            </p>
          </div>
        </div>
        {connectionIsActive && integrationActions()}
      </header>

      {loadingConnection ? (
        <section className="flex min-h-48 items-center justify-center rounded-lg border border-border bg-surface p-6">
          <div className="flex items-center gap-3 text-secondary">
            <Spinner size="sm" />
            <span>
              {t("workspace:integrations.shopify.loading_connection")}
            </span>
          </div>
        </section>
      ) : !connection ? (
        <section className="rounded-lg border border-border bg-surface p-6">
          <h2 className="text-lg font-semibold text-text">
            {t("workspace:integrations.shopify.connect_title")}
          </h2>
          {SHOPIFY_MANAGED_INSTALL ? (
            <>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-secondary">
                {t(
                  SHOPIFY_APP_STORE_URL
                    ? "workspace:integrations.shopify.install_description"
                    : "workspace:integrations.shopify.install_unavailable_description",
                )}
              </p>
              {SHOPIFY_APP_STORE_URL && (
                <div className="mt-5 flex justify-end">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => window.open(SHOPIFY_APP_STORE_URL, "_blank")}
                  >
                    {t("workspace:integrations.shopify.install")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-secondary">
                {t("workspace:integrations.shopify.connect_description")}
              </p>
              <form onSubmit={connect} className="mt-5 max-w-2xl space-y-4">
                <Label
                  htmlFor="shopify-store-domain"
                  value={t("workspace:integrations.shopify.shop_domain")}
                />
                <StyledTextInput
                  id="shopify-store-domain"
                  value={shopDomain}
                  onInputChanged={setShopDomain}
                  placeholder="butikk.myshopify.com"
                  className={SHOPIFY_CONTROL_CLASS}
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!shopDomain.trim() || busy}
                  >
                    {busy
                      ? t("workspace:integrations.shopify.connecting")
                      : t("workspace:integrations.shopify.connect")}
                  </Button>
                </div>
              </form>
            </>
          )}
        </section>
      ) : !connectionIsActive ? (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-6">
          <h2 className="text-lg font-semibold text-amber-950">
            {t("workspace:integrations.shopify.action_required")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-amber-900">
            {t("workspace:integrations.shopify.action_required_description")}
          </p>
          <p className="mt-2 text-sm text-amber-900">{connection.shopDomain}</p>
          <form onSubmit={connect} className="mt-5 flex justify-end">
            <Button type="submit" variant="primary" disabled={busy}>
              {busy
                ? t("workspace:integrations.shopify.connecting")
                : t("workspace:integrations.shopify.reconnect")}
            </Button>
          </form>
        </section>
      ) : (
        <>
          <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
            <TabButton
              label={t("workspace:integrations.shopify.general_tab")}
              isSelected={activeSettingsTab === "general"}
              onClick={() => setActiveSettingsTab("general")}
            />
            <TabButton
              label={t("workspace:integrations.shopify.products_title")}
              isSelected={activeSettingsTab === "products"}
              onClick={() => setActiveSettingsTab("products")}
            />
            <TabButton
              label={`${t("workspace:integrations.shopify.activity_tab")}${
                importHealth?.failedCount
                  ? ` (${importHealth.failedCount})`
                  : ""
              }`}
              isSelected={activeSettingsTab === "activity"}
              onClick={() => setActiveSettingsTab("activity")}
            />
          </ul>

          <section
            className={`rounded-lg border border-border bg-surface p-6 ${
              activeSettingsTab === "general" ? "" : "hidden"
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-text">
                  {t("workspace:integrations.shopify.transport_title")}
                </h2>
                <p className="mt-1 text-sm text-secondary">
                  {connection.shopDomain}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {saved && (
                  <span className="text-sm text-green-700">
                    {t("workspace:integrations.shopify.saved")}
                  </span>
                )}
                <Button
                  type="submit"
                  form="shopify-transport-settings"
                  variant="primary"
                  size="sm"
                  disabled={!pickupLocationId || busy}
                >
                  {t("common:save")}
                </Button>
              </div>
            </div>
            <form
              id="shopify-transport-settings"
              onSubmit={saveConfiguration}
              className="mt-4 divide-y divide-border"
            >
              <SettingsRow
                title={t("workspace:integrations.shopify.import_mode")}
                description={t(
                  "workspace:integrations.shopify.import_mode_tooltip",
                )}
              >
                <div className="w-80 max-w-[45vw]">
                  <StyledSelect
                    key={`import-mode-${importMode}`}
                    id="shopify-import-mode"
                    defaultValue={importMode}
                    className={SHOPIFY_CONTROL_CLASS}
                    onSelectChanged={(value) =>
                      setImportMode(value as "local_delivery" | "all_shippable")
                    }
                    options={[
                      {
                        key: "local_delivery",
                        label: t(
                          "workspace:integrations.shopify.import_mode_local",
                        ),
                      },
                      {
                        key: "all_shippable",
                        label: t(
                          "workspace:integrations.shopify.import_mode_all",
                        ),
                      },
                    ]}
                  />
                </div>
              </SettingsRow>
              <SettingsRow
                title={t("workspace:integrations.shopify.pickup")}
                description={t("workspace:integrations.shopify.pickup_tooltip")}
              >
                <div className="w-80 max-w-[45vw]">
                  <StyledSelect
                    key={`pickup-${pickupLocationId}`}
                    id="shopify-pickup"
                    emptyOption
                    emptyOptionLabel={t(
                      "workspace:integrations.shopify.select_pickup",
                    )}
                    defaultValue={pickupLocationId}
                    className={SHOPIFY_CONTROL_CLASS}
                    onSelectChanged={setPickupLocationId}
                    options={locations.map((location) => ({
                      key: location.id,
                      label:
                        location.displayName ||
                        `${location.addressLine}, ${location.city}`,
                    }))}
                  />
                </div>
              </SettingsRow>
              <SettingsRow
                title={t("workspace:integrations.shopify.driver")}
                description={t("workspace:integrations.shopify.driver_tooltip")}
              >
                <div className="w-80 max-w-[45vw]">
                  <StyledSelect
                    key={`driver-${defaultDriverId}`}
                    id="shopify-driver"
                    emptyOption
                    emptyOptionLabel={t(
                      "workspace:integrations.shopify.no_default_driver",
                    )}
                    defaultValue={defaultDriverId}
                    className={SHOPIFY_CONTROL_CLASS}
                    onSelectChanged={setDefaultDriverId}
                    options={drivers.map((driver) => ({
                      key: driver.id,
                      label: Driver.getDriverName(driver, true),
                    }))}
                  />
                </div>
              </SettingsRow>
              <SettingsRow
                title={t("workspace:integrations.shopify.project_number")}
                description={t(
                  "workspace:integrations.shopify.project_number_tooltip",
                )}
              >
                <div className="w-80 max-w-[45vw]">
                  <StyledTextInput
                    id="shopify-project"
                    value={defaultProjectNumber}
                    onInputChanged={setDefaultProjectNumber}
                    className={SHOPIFY_CONTROL_CLASS}
                  />
                </div>
              </SettingsRow>
            </form>
          </section>

          <section className={activeSettingsTab === "activity" ? "" : "hidden"}>
            <div className="rounded-lg border border-border bg-surface p-6">
              <h2 className="text-lg font-semibold text-text">
                {t("workspace:integrations.shopify.activity_title")}
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-secondary">
                {t("workspace:integrations.shopify.activity_description")}
              </p>

              <dl className="mt-5 text-sm">
                <div className="grid gap-2 border-t border-border py-3 sm:grid-cols-[14rem_minmax(0,1fr)]">
                  <dt className="text-secondary">
                    {t("workspace:integrations.shopify.import_status")}
                  </dt>
                  <dd className="flex items-center gap-2 text-text">
                    {loadingImportHealth ? (
                      <>
                        <Spinner size="sm" />
                        <span>
                          {t("workspace:integrations.shopify.health_loading")}
                        </span>
                      </>
                    ) : (
                      <>
                        <span
                          className={`h-2 w-2 rounded-full ${
                            displayedImportStatus === "healthy"
                              ? "bg-green-500"
                              : displayedImportStatus === "attention"
                                ? "bg-red-500"
                                : displayedImportStatus === "paused"
                                  ? "bg-gray-400"
                                  : "bg-amber-500"
                          }`}
                        />
                        {t(
                          `workspace:integrations.shopify.health_${displayedImportStatus}`,
                        )}
                      </>
                    )}
                  </dd>
                </div>
                <div className="grid gap-2 border-t border-border py-3 sm:grid-cols-[14rem_minmax(0,1fr)]">
                  <dt className="text-secondary">
                    {t("workspace:integrations.shopify.last_webhook")}
                  </dt>
                  <dd className="text-text">
                    {lastOrderReceivedAt
                      ? new Date(lastOrderReceivedAt).toLocaleString()
                      : t("workspace:integrations.shopify.no_import_yet")}
                  </dd>
                </div>
                <div className="grid gap-2 border-t border-border py-3 sm:grid-cols-[14rem_minmax(0,1fr)]">
                  <dt className="text-secondary">
                    {t("workspace:integrations.shopify.last_successful_import")}
                  </dt>
                  <dd className="text-text">
                    {lastSuccessfulImportAt
                      ? new Date(lastSuccessfulImportAt).toLocaleString()
                      : t("workspace:integrations.shopify.no_import_yet")}
                  </dd>
                </div>
                {importHealth?.events
                  .filter((event) => event.status === "failed")
                  .map((event) => (
                    <div
                      key={event.id}
                      className="grid gap-2 border-t border-border py-3 sm:grid-cols-[14rem_minmax(0,1fr)]"
                    >
                      <dt className="text-secondary">
                        {t("workspace:integrations.shopify.failed_imports")}
                      </dt>
                      <dd className="flex min-w-0 flex-wrap items-center justify-between gap-3 text-text">
                        <span>
                          {importErrorMessage(event.lastErrorCode)}
                          {event.receivedAt && (
                            <span className="text-secondary">
                              {" · "}
                              {new Date(event.receivedAt).toLocaleString()}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          className="font-medium text-brand hover:text-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() =>
                            event.retryable
                              ? retryImport(event.id)
                              : setDismissConfirmEventId(event.id)
                          }
                          disabled={Boolean(
                            retryingEventId ||
                              dismissingEventId ||
                              restoringEventId,
                          )}
                        >
                          {event.retryable
                            ? retryingEventId === event.id
                              ? t("workspace:integrations.shopify.retrying")
                              : t("workspace:integrations.shopify.retry")
                            : dismissingEventId === event.id
                              ? t("workspace:integrations.shopify.dismissing")
                              : t("workspace:integrations.shopify.dismiss")}
                        </button>
                      </dd>
                    </div>
                  ))}
                {importHealth?.events
                  .filter((event) => event.status === "dismissed")
                  .map((event) => (
                    <div
                      key={event.id}
                      className="grid gap-2 border-t border-border py-3 sm:grid-cols-[14rem_minmax(0,1fr)]"
                    >
                      <dt className="text-secondary">
                        {t("workspace:integrations.shopify.dismissed_imports")}
                      </dt>
                      <dd className="flex min-w-0 flex-wrap items-center justify-between gap-3 text-text">
                        <span>
                          {importErrorMessage(event.lastErrorCode)}
                          {event.receivedAt && (
                            <span className="text-secondary">
                              {" · "}
                              {new Date(event.receivedAt).toLocaleString()}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          className="font-medium text-brand hover:text-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => restoreImport(event.id)}
                          disabled={Boolean(
                            retryingEventId ||
                              dismissingEventId ||
                              restoringEventId,
                          )}
                        >
                          {restoringEventId === event.id
                            ? t("workspace:integrations.shopify.restoring")
                            : t("workspace:integrations.shopify.restore")}
                        </button>
                      </dd>
                    </div>
                  ))}
              </dl>
              {importHealthError && (
                <p className="border-t border-border pt-3 text-sm text-red-700">
                  {importHealthError}
                </p>
              )}
            </div>
          </section>

          <section className={activeSettingsTab === "products" ? "" : "hidden"}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-text">
                  {t("workspace:integrations.shopify.products_title")}
                </h2>
                <p className="mt-1 text-sm text-secondary">
                  {t("workspace:integrations.shopify.products_description")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {saved && (
                  <span className="text-sm text-green-700">
                    {t("workspace:integrations.shopify.saved")}
                  </span>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={loadProducts}
                  disabled={busy}
                >
                  {preview
                    ? t("workspace:integrations.shopify.refresh_products")
                    : t("workspace:integrations.shopify.load_products")}
                </Button>
                <Button
                  type="submit"
                  form="shopify-product-defaults"
                  variant="primary"
                  size="sm"
                  disabled={busy}
                >
                  {t("common:save")}
                </Button>
              </div>
            </div>
            <form
              id="shopify-product-defaults"
              onSubmit={saveConfiguration}
              className="mt-4 divide-y divide-border"
            >
              <SettingsRow
                title={t("workspace:integrations.shopify.default_product")}
                description={t(
                  "workspace:integrations.shopify.default_product_tooltip",
                )}
                separator={false}
              >
                <div className="w-80 max-w-[45vw]">
                  <StyledSelect
                    key={`default-product-${defaultProductId}`}
                    id="shopify-default-product"
                    emptyOption
                    emptyOptionLabel={t(
                      "workspace:integrations.shopify.no_default_product",
                    )}
                    defaultValue={defaultProductId}
                    className={SHOPIFY_CONTROL_CLASS}
                    onSelectChanged={setDefaultProductId}
                    options={products.map((product) => ({
                      key: product.id,
                      label: product.descriptionShort || product.id,
                    }))}
                  />
                </div>
              </SettingsRow>
            </form>
            {preview && (
              <div className="mt-6 border-t border-border pt-2">
                <div className="divide-y divide-border">
                  {preview.items.map((item) => (
                    <SettingsRow
                      key={item.variant.variantId}
                      title={`${item.variant.productTitle}${
                        item.variant.variantTitle &&
                        item.variant.variantTitle !== "Default Title"
                          ? ` – ${item.variant.variantTitle}`
                          : ""
                      }`}
                      description={
                        item.variant.sku ||
                        t("workspace:integrations.shopify.no_sku")
                      }
                    >
                      <div className="w-80 max-w-[45vw]">
                        <StyledSelect
                          key={`mapping-${item.variant.variantId}-${mappingValues[item.variant.variantId]}`}
                          id={`mapping-${item.variant.variantId}`}
                          emptyOption
                          emptyOptionLabel={t(
                            "workspace:integrations.shopify.no_transport_product",
                          )}
                          defaultValue={mappingValues[item.variant.variantId]}
                          className={SHOPIFY_CONTROL_CLASS}
                          onSelectChanged={(value) =>
                            setMappingValues((current) => ({
                              ...current,
                              [item.variant.variantId]: value,
                            }))
                          }
                          options={[
                            ...products.map((product) => ({
                              key: product.id,
                              label: product.descriptionShort || product.id,
                            })),
                            {
                              key: "__ignored__",
                              label: t(
                                "workspace:integrations.shopify.ignore_product",
                              ),
                            },
                          ]}
                        />
                      </div>
                    </SettingsRow>
                  ))}
                </div>
                <div className="flex justify-end pt-4">
                  <Button
                    variant="primary"
                    onClick={saveMappings}
                    disabled={busy}
                  >
                    {t("workspace:integrations.shopify.save_products")}
                  </Button>
                </div>
              </div>
            )}
          </section>
        </>
      )}
      {error && (
        <p
          role="alert"
          className="rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default ShopifyIntegrationContent;
